/**
 * paymentMessaging — context-aware pre-order/payment explanatory message.
 *
 * Ports the v2 booking-form logic (`BookingDetailsProvider.preorderAndPaymentMsg`)
 * so guests see clear prose, on the customer-details step, explaining what they
 * need to do and why — not just a numerical summary.
 *
 * The message is derived purely from booking configuration (pre-order menus,
 * the booking type's pre-order payment requirement and the deposit type), all of
 * which is available before the guest pre-orders or pays. The function is pure:
 * it returns a descriptor (translation key, default British-English string and
 * interpolation values) and never touches i18next or the DOM.
 */

/** Deposit kind from `bookingOptions.depositRequired.type`. */
export type DepositType = "payment" | "auth" | "authenticate" | "deposit" | null | undefined;

export interface PreorderAndPaymentMessageParams {
  /** Whether the selected booking type has any pre-order menus. */
  readonly hasMenus: boolean;
  /** Whether a pre-order is mandatory (a menu has a minimum pre-order amount). */
  readonly preorderIsRequired: boolean;
  /** The booking type's pre-order payment requirement (e.g. "full_amount", "percentage"). */
  readonly preorderPaymentRequired?: string | null;
  /** The deposit type required to secure the booking, if any. */
  readonly depositType: DepositType;
  /** Formatted deposit amount, e.g. "£50.00". */
  readonly amount: string;
  /** Whether the deposit should be taken with the preorder payment. */
  readonly depositTakeWithPreorder?: boolean;
}

export interface PaymentMessageDescriptor {
  /** i18next translation key. */
  readonly key: string;
  /** Default British-English string (used when the key is not in resources). */
  readonly defaultValue: string;
  /** Interpolation values forwarded to `t()`. */
  readonly values?: Record<string, string>;
}

/** Default British-English strings, matching the v2 booking-form wording. */
const MESSAGE_DEFAULTS: Record<string, string> = {
  booking_msg_pre_order_and_deposit_required:
    "You will need to pre-order some items and pay a deposit for your chosen items in order to complete this booking.",
  booking_msg_pre_order_and_payment_required:
    "You will need to pre-order some items and pay for your chosen items in order to complete this booking.",
  booking_msg_pre_order_required: "You will need to pre-order some items in order to complete this booking.",
  booking_msg_payment_required: "To secure this booking, we will need to take a payment of {{amount}}.",
  booking_msg_deposit_required:
    "To secure this booking, we will need to hold your card details for an amount of {{amount}}. We hold your card details, but no money will be taken unless you fail to turn up for your booking or break the booking terms.",
  booking_msg_pre_order_or_deposit_required:
    "To secure this booking, you will either need to pre-order and pay for some items, or we will need to take a payment of {{amount}}.",
  booking_msg_pre_order_or_auth_deposit_required:
    "To secure this booking, you will either need to pre-order and pay for some items, or we will need to hold your card details for a deposit of {{amount}}. If we hold your card details, no money will be taken unless you fail to turn up for your booking or violate the booking terms.",
};

function descriptor(key: string, values?: Record<string, string>): PaymentMessageDescriptor {
  return { key, defaultValue: MESSAGE_DEFAULTS[key], values };
}

interface PaymentRequirements {
  readonly requiresFullPayment: boolean;
  readonly requiresPartPayment: boolean;
  readonly requiresNoPayment: boolean;
}

function getPaymentRequirements(hasMenus: boolean, preorderPaymentRequired?: string | null): PaymentRequirements {
  const requiresFullPayment = hasMenus && preorderPaymentRequired === "full_amount";
  const requiresPartPayment = hasMenus && preorderPaymentRequired === "percentage";
  const requiresNoPayment = hasMenus && !requiresFullPayment && !requiresPartPayment;

  return { requiresFullPayment, requiresPartPayment, requiresNoPayment };
}

// Helper functions to check specific conditions
export function shouldSkipDepositWithPreorder(depositType: DepositType, depositTakeWithPreorder?: boolean): boolean {
  return depositType === "payment" && !depositTakeWithPreorder;
}

export function shouldShowDepositOrPreorderChoice(
  preorderIsRequired: boolean,
  depositType: DepositType,
  preorderPaymentRequired?: string | null,
  depositTakeWithPreorder?: boolean,
): boolean {
  // Pre-ordering is only a genuine alternative to the deposit payment when
  // paying for the pre-order is actually possible. If it's pay-at-venue
  // (payment not required), there is no real choice to present. Likewise, when
  // the deposit is bundled with the pre-order payment (takeWithPreorder), the
  // deposit is always taken, so pre-ordering is not an alternative to it.
  return (
    !preorderIsRequired &&
    !!preorderPaymentRequired &&
    shouldSkipDepositWithPreorder(depositType, depositTakeWithPreorder)
  );
}

export function shouldShowPaymentOnly(
  preorderIsRequired: boolean,
  depositType: DepositType,
  preorderPaymentRequired?: string | null,
): boolean {
  return preorderIsRequired && depositType === "payment" && !preorderPaymentRequired;
}

/**
 * A mandatory pre-order that requires no payment (pay-at-venue) still needs to
 * secure the booking via card authentication/auth-hold. Mirrors
 * `shouldShowPaymentOnly` for the auth/authenticate deposit types so the guest
 * is told about the authentication, not just the pre-order.
 */
export function shouldShowAuthOnly(
  preorderIsRequired: boolean,
  depositType: DepositType,
  preorderPaymentRequired?: string | null,
): boolean {
  return preorderIsRequired && (depositType === "auth" || depositType === "authenticate") && !preorderPaymentRequired;
}

export function shouldShowAuthDepositOrPreorderChoice(
  preorderIsRequired: boolean,
  depositType: DepositType,
  preorderPaymentRequired?: string | null,
): boolean {
  // Mirrors shouldShowDepositOrPreorderChoice: pre-ordering is only a genuine
  // alternative to the card hold when paying for the pre-order is possible.
  return !preorderIsRequired && (depositType === "auth" || depositType === "authenticate") && !!preorderPaymentRequired;
}

export function handleEdgeCases(
  hasMenus: boolean,
  preorderIsRequired: boolean,
  preorderPaymentRequired?: string | null,
  depositType?: DepositType,
  depositTakeWithPreorder?: boolean,
  amount?: string,
): PaymentMessageDescriptor | null {
  const amountValues: Record<string, string> = amount ? { amount } : {};

  if (!hasMenus) {
    return null;
  }

  if (!depositType) {
    return null;
  }

  // The "either pre-order-and-pay or we take a deposit" framing only makes sense
  // when the pre-order is optional, paying for it is actually possible, and the
  // deposit isn't bundled with the pre-order payment — otherwise the guest has
  // no real choice to make, so this must not override the more specific
  // messages below.
  if (
    shouldShowDepositOrPreorderChoice(preorderIsRequired, depositType, preorderPaymentRequired, depositTakeWithPreorder)
  ) {
    return descriptor("booking_msg_pre_order_or_deposit_required", amountValues);
  }

  if (shouldShowPaymentOnly(preorderIsRequired, depositType, preorderPaymentRequired)) {
    return descriptor("booking_msg_payment_required", amountValues);
  }

  if (shouldShowAuthOnly(preorderIsRequired, depositType, preorderPaymentRequired)) {
    return descriptor("booking_msg_deposit_required", amountValues);
  }

  if (shouldShowAuthDepositOrPreorderChoice(preorderIsRequired, depositType, preorderPaymentRequired)) {
    return descriptor("booking_msg_pre_order_or_auth_deposit_required", amountValues);
  }

  return null;
}

// Decision matrix for message selection
export function selectMessageByScenario(
  hasMenus: boolean,
  preorderIsRequired: boolean,
  requirements: PaymentRequirements,
  depositType?: DepositType,
  amount?: string,
): PaymentMessageDescriptor | null {
  const amountValues: Record<string, string> = amount ? { amount } : {};

  // Scenario 1: Pre-order required with payment
  if (hasMenus && preorderIsRequired && requirements.requiresFullPayment) {
    return descriptor("booking_msg_pre_order_and_payment_required");
  }

  // Scenario 2: Pre-order required with deposit
  if (hasMenus && preorderIsRequired && requirements.requiresPartPayment) {
    return descriptor("booking_msg_pre_order_and_deposit_required");
  }

  // Scenario 3: Pre-order required with no payment
  if (hasMenus && preorderIsRequired && requirements.requiresNoPayment) {
    return descriptor("booking_msg_pre_order_required");
  }

  // Scenario 4: Payment deposit only
  if (depositType === "payment") {
    return descriptor("booking_msg_payment_required", amountValues);
  }

  // Scenario 5: Auth/authenticate deposit
  if (depositType === "auth" || depositType === "authenticate") {
    return descriptor("booking_msg_deposit_required", amountValues);
  }

  return null;
}

/**
 * Resolve the context-aware pre-order/payment message, or `null` when no message
 * applies (no required pre-order and no deposit/payment).
 */
export function getPreorderAndPaymentMessage(params: PreorderAndPaymentMessageParams): PaymentMessageDescriptor | null {
  const { hasMenus, preorderIsRequired, preorderPaymentRequired, depositType, amount, depositTakeWithPreorder } =
    params;

  const requirements = getPaymentRequirements(hasMenus, preorderPaymentRequired);

  // Check edge cases first
  const edgeCaseMessage = handleEdgeCases(
    hasMenus,
    preorderIsRequired,
    preorderPaymentRequired,
    depositType,
    depositTakeWithPreorder,
    amount,
  );

  if (edgeCaseMessage) {
    return edgeCaseMessage;
  }

  // Use decision matrix for standard scenarios
  return selectMessageByScenario(hasMenus, preorderIsRequired, requirements, depositType, amount);
}
